/**
 * Billing API hooks — BOLT-139: Customer Portal & Billing Status API.
 *
 * React Query hooks for billing endpoints:
 *   - GET  /v1/billing/status            — current billing status
 *   - POST /v1/billing/portal-session    — create billing portal session
 */
import { useQuery, useMutation } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client.js';
import { useAuthStore } from '@/store/auth.js';

// ---------------------------------------------------------------------------
// Response shapes
// ---------------------------------------------------------------------------

export interface BillingSubscription {
  status: string;
  current_period_end: string | null;
  plan: string | null;
}

/** BOLT-354: one billing card per business under the org. */
export interface BusinessBilling {
  business_id: string;
  name: string;
  status: string;
  subscription: BillingSubscription | null;
  portal_url_available: boolean;
}

export interface BillingStatusResponse {
  org_status: string;
  /** Back-compat: mirrors the primary business's subscription. */
  subscription: BillingSubscription | null;
  portal_url_available: boolean;
  /** BOLT-354: per-business billing, one entry per business (>=1). */
  businesses: BusinessBilling[];
}

export interface PortalSessionResponse {
  portal_url: string;
}

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

export function useBillingStatus() {
  const orgId = useAuthStore((s) => s.orgId);
  return useQuery({
    queryKey: ['billingStatus', orgId],
    queryFn: () => apiClient.get<BillingStatusResponse>('/v1/billing/status'),
  });
}

export function useCreatePortalSession() {
  return useMutation({
    // BOLT-354: business-scoped. Omitting business_id targets the primary business.
    mutationFn: (businessId?: string) =>
      apiClient.post<PortalSessionResponse>(
        '/v1/billing/portal-session',
        businessId ? { body: { business_id: businessId } } : undefined,
      ),
  });
}
